import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ChatBox from "../components/ChatBox";
import { ImaController } from "../lib/imaController";
import "../Stream.css";

const API = process.env.REACT_APP_API_URL || "/api";

const HILLTOP_PREROLL_TAG =
  "https://pointless-plenty.com/d-m_F/zKd.GLNuvLZdGDUF/ae/mV9euAZ/UwlukFPSTcY/4/OFDxEj5uOyD/EDtANjj/g_4GMMTZkk4ZN/SMZssGabW/1CpedIDE0CxL";

const DEFAULT_PREROLL_TAG =
  process.env.REACT_APP_IMA_AD_TAG_URL?.trim() || HILLTOP_PREROLL_TAG;

const MIDROLL_TAG = process.env.REACT_APP_IMA_MIDROLL_TAG_URL?.trim() || "";

const withCorrelator = (base) => {
  if (!base) return "";
  if (base.includes("correlator=")) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}correlator=${Date.now()}`;
};

export default function StreamPlayer() {
  const { id } = useParams();

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const imaRef = useRef(null);

  const prerollPlayedRef = useRef(false);
  const prerollStartInFlightRef = useRef(false);
  const midrollTimerRef = useRef(null);
  const midrollInFlightRef = useRef(false);
  const imaGestureReadyRef = useRef(false);
  const lastSrcRef = useRef("");

  const channelId = useMemo(() => id || "", [id]);
  const room = useMemo(() => `channel:${channelId || "unknown"}`, [channelId]);

  const [state, setState] = useState({
    src: "",
    title: "",
    isLive: false,
    loading: true,
    error: "",
  });

  const [subStatus, setSubStatus] = useState("");
  const [subLoading, setSubLoading] = useState(true);
  const [showStartOverlay, setShowStartOverlay] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setSubLoading(true);
        const res = await fetch(`${API}/subscriptions/me`, {
          credentials: "include",
        });
        const j = await res.json().catch(() => ({}));
        const status =
          j?.status ||
          j?.membership?.status ||
          j?.member?.status ||
          j?.subscription?.status ||
          "";

        if (!alive) return;
        setSubStatus(String(status || ""));
      } catch {
        if (!alive) return;
        setSubStatus("");
      } finally {
        if (!alive) return;
        setSubLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const isPaidSubscriber = useMemo(
    () => String(subStatus || "").toLowerCase() === "active",
    [subStatus]
  );

  const shouldShowAds = useMemo(() => {
    if (subLoading) return true;
    return !isPaidSubscriber;
  }, [subLoading, isPaidSubscriber]);

  const PREROLL_KEY = useMemo(() => `ga_preroll_${channelId}`, [channelId]);
  const MIDROLL_KEY = useMemo(() => `ga_midroll_${channelId}`, [channelId]);
  const MIDROLL_MS = 8 * 60 * 1000;

  const logAds = (...args) => {
    console.log("[ADS]", ...args);
  };

  useEffect(() => {
    let alive = true;

    const tick = async () => {
      try {
        if (!channelId) return;

        const res = await fetch(`${API}/streams/${encodeURIComponent(channelId)}`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error("Stream not found");

        const data = await res.json().catch(() => ({}));
        const channel = data?.channel || {};
        const playbackId =
          data?.player?.playbackId ||
          channel?.playbackId ||
          channel?.lastVodPlaybackId ||
          channel?.lastVodPlayback ||
          "";

        const isLive = Boolean(
          data?.player?.isLive ?? data?.isLive ?? channel?.isLive ?? false
        );

        const src = playbackId
          ? `https://stream.mux.com/${playbackId}.m3u8`
          : "";

        const title =
          data?.channel?.streamMeta?.title ||
          data?.streamMeta?.title ||
          channel?.name ||
          channel?.title ||
          `Channel ${channelId}`;

        if (!alive) return;

        const srcChanged = src && src !== lastSrcRef.current;

        if (srcChanged) {
          lastSrcRef.current = src;
          prerollPlayedRef.current = false;
          prerollStartInFlightRef.current = false;
          imaGestureReadyRef.current = false;

          try {
            sessionStorage.removeItem(PREROLL_KEY);
          } catch {}

          setShowStartOverlay(true);

          setState({
            src,
            title,
            isLive,
            loading: false,
            error: src ? "" : "No stream source available.",
          });
        } else {
          setState((prev) => ({
            ...prev,
            title,
            isLive,
            loading: false,
            error: prev.src ? "" : "No stream source available.",
          }));
        }
      } catch (e) {
        if (!alive) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: e?.message || "Failed to load stream.",
        }));
      }
    };

    tick();
    const intervalId = setInterval(tick, 8000);

    return () => {
      alive = false;
      clearInterval(intervalId);
    };
  }, [channelId, PREROLL_KEY]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const videoEl = videoRef.current;
      const src = state.src;

      try {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      } catch {}

      if (!videoEl || !src) return;

      const canNative = videoEl.canPlayType("application/vnd.apple.mpegurl");
      if (canNative) {
        videoEl.src = src;
        return;
      }

      const mod = await import("hls.js");
      const Hls = mod.default;

      if (!Hls.isSupported()) {
        videoEl.src = src;
        return;
      }

      const hls = new Hls({
        lowLatencyMode: true,
        backBufferLength: 90,
      });

      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(videoEl);

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!alive) return;
        logAds("HLS error", data?.type, data?.details);
      });
    })();

    return () => {
      alive = false;
      try {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      } catch {}
    };
  }, [state.src]);

  useEffect(() => {
    const videoEl = videoRef.current;
    const adContainerEl = document.getElementById("ima-ad-container");

    try {
      if (imaRef.current) {
        imaRef.current.destroy();
        imaRef.current = null;
      }
    } catch {}

    if (!videoEl || !adContainerEl) return;
    if (!shouldShowAds) return;
    if (!state.src) return;
    if (!DEFAULT_PREROLL_TAG) return;

    const ctrl = new ImaController({
      videoEl,
      adContainerEl,
      adTagUrl: () => withCorrelator(DEFAULT_PREROLL_TAG),
      onLog: logAds,
    });

    imaRef.current = ctrl;

    ctrl.init().catch((e) => {
      logAds("IMA init failed", e);
    });

    return () => {
      try {
        ctrl.destroy();
      } catch {}
    };
  }, [shouldShowAds, state.src]);

  const handleStartClick = async () => {
    const videoEl = videoRef.current;
    const ctrl = imaRef.current;

    if (!videoEl || !state.src) return;
    if (prerollStartInFlightRef.current) return;

    prerollStartInFlightRef.current = true;

    try {
      if (!shouldShowAds || !ctrl) {
        logAds("Preroll bypass -> normal playback", {
          shouldShowAds,
          hasCtrl: !!ctrl,
          hasSrc: !!state.src,
        });
        setShowStartOverlay(false);
        await videoEl.play().catch(() => {});
        return;
      }

      try {
        if (sessionStorage.getItem(PREROLL_KEY) === "1") {
          prerollPlayedRef.current = true;
          logAds("Preroll already played for session");
          setShowStartOverlay(false);
          await videoEl.play().catch(() => {});
          return;
        }
      } catch {}

      if (!imaGestureReadyRef.current) {
        ctrl.initializeForUserGesture();
        imaGestureReadyRef.current = true;
        logAds("IMA gesture initialized", "overlay-button");
      }

      if (!prerollPlayedRef.current) {
        prerollPlayedRef.current = true;
        try {
          sessionStorage.setItem(PREROLL_KEY, "1");
        } catch {}

        logAds("Starting preroll request", "overlay-button");
        await ctrl.requestAndPlayPreroll();
      }

      setShowStartOverlay(false);
      await videoEl.play().catch(() => {});
    } catch (e) {
      logAds("Preroll request failed", e);
      setShowStartOverlay(false);
      await videoEl.play().catch(() => {});
    } finally {
      prerollStartInFlightRef.current = false;
    }
  };

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    if (!MIDROLL_TAG) return;
    if (!shouldShowAds) return;

    const schedule = () => {
      if (!imaRef.current) return;

      try {
        if (sessionStorage.getItem(MIDROLL_KEY) === "1") return;
      } catch {}

      try {
        if (midrollTimerRef.current) clearTimeout(midrollTimerRef.current);
      } catch {}

      midrollTimerRef.current = setTimeout(async () => {
        if (midrollInFlightRef.current) return;
        midrollInFlightRef.current = true;

        try {
          if (!imaRef.current) return;
          await imaRef.current.requestAndPlayAd(withCorrelator(MIDROLL_TAG));
          try {
            sessionStorage.setItem(MIDROLL_KEY, "1");
          } catch {}
        } finally {
          midrollInFlightRef.current = false;
        }
      }, MIDROLL_MS);
    };

    const onPlay = () => schedule();
    const onPause = () => {
      try {
        if (midrollTimerRef.current) clearTimeout(midrollTimerRef.current);
      } catch {}
      midrollTimerRef.current = null;
    };

    videoEl.addEventListener("play", onPlay);
    videoEl.addEventListener("pause", onPause);

    if (!videoEl.paused) schedule();

    return () => {
      try {
        if (midrollTimerRef.current) clearTimeout(midrollTimerRef.current);
      } catch {}
      midrollTimerRef.current = null;
      midrollInFlightRef.current = false;

      videoEl.removeEventListener("play", onPlay);
      videoEl.removeEventListener("pause", onPause);
    };
  }, [shouldShowAds, MIDROLL_KEY, MIDROLL_MS]);

  const layoutWrap = { width: "100%" };

  const grid = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 360px",
    gap: 12,
    alignItems: "start",
  };

  const chatSticky = {
    position: "sticky",
    top: "calc(var(--ga-header-clear) - 16px)",
  };

  const chatCard = {
    overflow: "visible",
  };

  const playerShellStyle = {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    maxHeight: "min(70vh, 520px)",
    background: "#000",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const playerFillStyle = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
  };

  return (
    <div style={layoutWrap}>
      <div style={{ marginBottom: 8 }}>
        <Link
          to="/streams"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            borderRadius: 999,
            textDecoration: "none",
            color: "#fff",
            background: "rgba(0,0,0,0.45)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          ← Channels
        </Link>
      </div>

      <div style={grid}>
        <div style={{ minWidth: 0 }}>
          <div style={{ marginBottom: 8, color: "rgba(255,255,255,0.75)" }}>
            {state.title} {state.isLive ? "• LIVE" : ""}
          </div>

          {state.loading ? (
            <div
              style={{
                ...playerShellStyle,
                display: "grid",
                placeItems: "center",
                color: "rgba(255,255,255,0.75)",
              }}
            >
              Loading stream…
            </div>
          ) : state.error ? (
            <div
              style={{
                ...playerShellStyle,
                display: "grid",
                placeItems: "center",
                color: "rgba(255,255,255,0.75)",
                padding: 16,
                textAlign: "center",
              }}
            >
              {state.error}
            </div>
          ) : (
            <div style={playerShellStyle}>
              <video
                ref={videoRef}
                controls
                playsInline
                preload="none"
                style={{
                  ...playerFillStyle,
                  display: "block",
                  background: "black",
                  objectFit: "contain",
                }}
              />

              <div
                id="ima-ad-container"
                style={{
                  ...playerFillStyle,
                  zIndex: 5,
                  pointerEvents: "none",
                }}
              />

              {showStartOverlay && (
                <button
                  type="button"
                  onClick={handleStartClick}
                  style={{
                    ...playerFillStyle,
                    zIndex: 10,
                    border: "none",
                    background: "rgba(0,0,0,0.35)",
                    color: "#fff",
                    fontSize: 22,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Click to start stream
                </button>
              )}
            </div>
          )}
        </div>

        <div style={chatSticky}>
          <div style={{ marginBottom: 8, color: "rgba(255,255,255,0.75)" }}>
            Chat room: {room}
          </div>

          <div style={chatCard}>
            <ChatBox room={room} />
          </div>
        </div>
      </div>
    </div>
  );
}